import { useEffect, useState } from "react";

export default function useDashboardUiState({ dashboardPrefs, updateDashboardPrefs, updateDashboardPrefsBatch, openConfirm, theme }) {
  const defaultBannerMsg = { type: "default", text: "", source: "", x: 0, y: 60, isRightAligned: true };
  const [bannerMessage, setBannerMessageLocal] = useState(defaultBannerMsg);

  useEffect(() => {
    if (dashboardPrefs.bannerMessage && dashboardPrefs.bannerMessage.type) {
      setBannerMessageLocal(dashboardPrefs.bannerMessage);
    }
  }, [dashboardPrefs.bannerMessage]);

  const setBannerMessage = (newMsgOrFn) => {
    setBannerMessageLocal((prev) => {
      const newMsg = typeof newMsgOrFn === "function" ? newMsgOrFn(prev) : newMsgOrFn;
      updateDashboardPrefs("bannerMessage", newMsg);
      return newMsg;
    });
  };

  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageForm, setMessageForm] = useState({ text: "", source: "" });

  const defaultWidgets = { students: true, attendance: true, birthday: true, budget: true, schedule: true, radar: true, goals: true, todo: true, growth: true };
  const [visibleWidgets, setVisibleWidgetsLocal] = useState(defaultWidgets);

  useEffect(() => {
    if (dashboardPrefs.widgets && Object.keys(dashboardPrefs.widgets).length > 0) {
      setVisibleWidgetsLocal((prev) => ({ ...defaultWidgets, ...prev, ...dashboardPrefs.widgets }));
    }
  }, [dashboardPrefs.widgets]);

  const setVisibleWidgets = (newWidgetsOrFn) => {
    setVisibleWidgetsLocal((prev) => {
      const newWidgets = typeof newWidgetsOrFn === "function" ? newWidgetsOrFn(prev) : newWidgetsOrFn;
      updateDashboardPrefs("widgets", newWidgets);
      return newWidgets;
    });
  };

  const toggleWidget = (id) => {
    setVisibleWidgets((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const defaultLayout = [
    { i: "banner", x: 0, y: 0, w: 15, h: 10 },
    { i: "students", x: 0, y: 10, w: 2, h: 6 },
    { i: "attendance", x: 2, y: 10, w: 2, h: 6 },
    { i: "birthday", x: 4, y: 10, w: 2, h: 6 },
    { i: "budget", x: 6, y: 10, w: 2, h: 6 },
    { i: "growth", x: 8, y: 10, w: 2, h: 6 },
    { i: "goals", x: 10, y: 10, w: 5, h: 6 },
    { i: "schedule", x: 0, y: 16, w: 5, h: 12 },
    { i: "radar", x: 5, y: 16, w: 5, h: 12 },
    { i: "todo", x: 10, y: 16, w: 5, h: 12 },
  ];

  const mobileLayout = [
    { i: "banner", x: 0, y: 0, w: 4, h: 10 },
    { i: "students", x: 0, y: 10, w: 4, h: 5 },
    { i: "attendance", x: 0, y: 15, w: 4, h: 5 },
    { i: "birthday", x: 0, y: 20, w: 4, h: 5 },
    { i: "budget", x: 0, y: 25, w: 4, h: 5 },
    { i: "schedule", x: 0, y: 30, w: 4, h: 8 },
    { i: "todo", x: 0, y: 38, w: 4, h: 8 },
    { i: "radar", x: 0, y: 46, w: 4, h: 10 },
    { i: "goals", x: 0, y: 56, w: 4, h: 8 },
  ];

  const defaultFullLayouts = { lg: defaultLayout, md: defaultLayout, sm: defaultLayout, xs: mobileLayout, xxs: mobileLayout };
  const [layouts, setLayoutsLocal] = useState(defaultFullLayouts);

  useEffect(() => {
    if (dashboardPrefs.layouts && Object.keys(dashboardPrefs.layouts).length > 0) {
      const parsed = { ...dashboardPrefs.layouts };

      ["lg", "md", "sm"].forEach((bp) => {
        if (parsed[bp]) {
          defaultLayout.forEach((defaultItem) => {
            const existingIdx = parsed[bp].findIndex((item) => item.i === defaultItem.i);
            if (existingIdx === -1) {
              parsed[bp].push(defaultItem);
            } else {
              const existing = parsed[bp][existingIdx];
              if (defaultItem.i === "goals" && (existing.w < 3 || existing.h < 4)) {
                existing.w = defaultItem.w;
                existing.h = defaultItem.h;
              }
            }
          });
        } else {
          parsed[bp] = defaultLayout;
        }
      });

      parsed.xs = mobileLayout;
      parsed.xxs = mobileLayout;
      setLayoutsLocal(parsed);
    }
  }, [dashboardPrefs.layouts]);

  useEffect(() => {
    if (window.innerWidth < 768) {
      setLayoutsLocal((prev) => ({
        ...prev,
        xs: mobileLayout,
        xxs: mobileLayout,
      }));
    }
  }, []);

  const [isEditMode, setIsEditMode] = useState(false);

  const onLayoutChange = (layout, newLayouts) => {
    setLayoutsLocal(newLayouts);
    updateDashboardPrefs("layouts", newLayouts);
  };

  const [currentBreakpoint, setCurrentBreakpoint] = useState("lg");

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let newBp = "lg";
      if (width < 480) newBp = "xxs";
      else if (width < 768) newBp = "xs";
      else if (width < 996) newBp = "sm";
      else if (width < 1200) newBp = "md";

      setCurrentBreakpoint(newBp);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onBreakpointChange = (breakpoint) => {
    setCurrentBreakpoint(breakpoint);
  };

  const getWidgetHeight = (id) => {
    const layout = layouts[currentBreakpoint] || [];
    const item = layout.find((v) => v.i === id);
    return item ? Math.floor(item.h / 2) : 1;
  };

  const deleteWidget = (id) => {
    openConfirm("위젯 숨기기", "이 위젯을 홈 화면에서 숨기시겠습니까?\n(설정에서 다시 켤 수 있습니다)", () =>
      setVisibleWidgets((prev) => ({ ...prev, [id]: false }))
    );
  };

  const resetLayout = () => {
    openConfirm(
      "레이아웃 초기화",
      "대시보드 배치와 위젯 설정을 초기 상태로 되돌리시겠습니까?",
      () => {
        const resetLayouts = { lg: defaultLayout, md: defaultLayout, sm: defaultLayout, xs: mobileLayout, xxs: mobileLayout };
        setLayoutsLocal(resetLayouts);

        const resetWidgets = { students: true, attendance: true, birthday: true, budget: true, schedule: true, radar: true, goals: true, todo: true, growth: true };
        setVisibleWidgetsLocal(resetWidgets);

        const resetBanner = { type: "default", text: "", source: "", x: 0, y: 60, isRightAligned: true };
        setBannerMessageLocal(resetBanner);

        if (typeof updateDashboardPrefsBatch === "function") {
          updateDashboardPrefsBatch({
            layouts: resetLayouts,
            widgets: resetWidgets,
            bannerMessage: resetBanner,
          });
        } else {
          updateDashboardPrefs("layouts", resetLayouts);
          updateDashboardPrefs("widgets", resetWidgets);
          updateDashboardPrefs("bannerMessage", resetBanner);
        }
      },
      null,
      { confirmButtonClass: `${theme.bg} text-white shadow-lg ${theme.shadow}/30 hover:opacity-90` }
    );
  };

  const [isDragging, setIsDragging] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    bannerMessage,
    setBannerMessage,
    isMessageModalOpen,
    setIsMessageModalOpen,
    messageForm,
    setMessageForm,
    visibleWidgets,
    toggleWidget,
    layouts,
    isEditMode,
    setIsEditMode,
    onLayoutChange,
    currentBreakpoint,
    onBreakpointChange,
    getWidgetHeight,
    deleteWidget,
    resetLayout,
    isDragging,
    setIsDragging,
    isMobile,
  };
}
